import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Wand2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";

const NONE = "__none__";

export const MappingDialog = ({ open, onOpenChange, file, preview, period, onImported }) => {
  const [mapping, setMapping] = useState(null);
  const [saveMapping, setSaveMapping] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (preview?.mapping) {
      setMapping({ credit_positive: true, ...preview.mapping });
      setSaveMapping(true);
    }
  }, [preview]);

  if (!preview || !mapping) return null;

  const cols = preview.columns || [];
  const set = (k, v) => setMapping((m) => ({ ...m, [k]: v === NONE ? null : v }));

  const ColSelect = ({ field, label, allowNone = true }) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <Select value={mapping[field] || NONE} onValueChange={(v) => set(field, v)}>
        <SelectTrigger data-testid={`map-${field}`} className="h-9">
          <SelectValue placeholder="Select column" />
        </SelectTrigger>
        <SelectContent>
          {allowNone && <SelectItem value={NONE}>— None —</SelectItem>}
          {cols.map((c) => (
            <SelectItem key={c} value={c}>{c}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const doImport = async () => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("mapping", JSON.stringify(mapping));
    fd.append("save", String(saveMapping));
    fd.append("period", period || "");
    setBusy(true);
    try {
      const res = await api.uploadMapped(fd);
      toast.success(`${res.inserted} transactions imported & categorized`);
      onOpenChange(false);
      onImported?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mapping-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Wand2 className="h-5 w-5" /> Map your columns
          </DialogTitle>
          <DialogDescription>
            {preview.saved
              ? "We found a saved mapping for this file format and applied it. Adjust if needed."
              : "Tell us which column is which so we import every transaction correctly."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <ColSelect field="date" label="Date column" />
            <ColSelect field="description" label="Description column" allowNone={false} />
          </div>

          <div>
            <Label className="text-xs">Amount format</Label>
            <Select value={mapping.mode} onValueChange={(v) => set("mode", v)}>
              <SelectTrigger data-testid="map-mode" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="separate">Separate Debit &amp; Credit columns</SelectItem>
                <SelectItem value="single">Single Amount column (+/-)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {mapping.mode === "separate" ? (
            <div className="grid grid-cols-2 gap-3">
              <ColSelect field="debit" label="Debit / money-out column" />
              <ColSelect field="credit" label="Credit / money-in column" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 items-end">
              <ColSelect field="amount" label="Amount column" allowNone={false} />
              <div className="flex items-center gap-2 pb-2">
                <Switch
                  data-testid="map-credit-positive"
                  checked={mapping.credit_positive}
                  onCheckedChange={(v) => set("credit_positive", v)}
                />
                <span className="text-xs text-gray-600">
                  Positive = income (credit)
                </span>
              </div>
            </div>
          )}

          {preview.sample_rows?.length > 0 && (
            <div className="border border-gray-200 rounded-lg overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    {cols.map((c) => (
                      <th key={c} className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.sample_rows.slice(0, 3).map((r, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      {cols.map((c) => (
                        <td key={c} className="px-3 py-1.5 text-gray-700 whitespace-nowrap">{r[c]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Switch
              data-testid="save-mapping-switch"
              checked={saveMapping}
              onCheckedChange={setSaveMapping}
            />
            <span className="text-sm text-gray-600">
              Remember this mapping for files with the same columns
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="bg-white border-gray-200"
          >
            Cancel
          </Button>
          <Button
            data-testid="confirm-import-button"
            onClick={doImport}
            disabled={busy}
            className="bg-black text-white hover:bg-gray-800 hover:text-white"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
            Import transactions
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
